/**
 * /rate-sheets/new — Create a rate sheet with labor, material, equipment rows.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button, IconButton, MenuItem, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { api } from "@/lib/api";
import { rateSheetKeys } from "@/lib/queries";
import { MoneyField } from "@/components/MoneyField";
import { useToast } from "@/components/Toaster";
import type { RateSheet } from "@/lib/types";
import { ProjectIdSelect, STANDARD_PROJECT_STATUSES } from "@/components/ProjectIdSelect";

export const Route = createFileRoute("/_auth/rate-sheets/new")({
  component: RateSheetNewPage,
});

interface DraftLabor {
  uid: string;
  craft: string;
  billing_method: string;
  regular_rate: string;
  ot_multiplier: string;
  dt_multiplier: string;
  markup_percent: string;
}

interface DraftMaterial {
  uid: string;
  category: string;
  markup_percent: string;
  tax_billable: boolean;
}

interface DraftEquipment {
  uid: string;
  equipment_class: string;
  rate_basis: string;
  rate: string;
}

let _uid = 0;
const uid = () => String(++_uid);

function RateSheetNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [rateType, setRateType] = useState("stipulated");
  const [projectId, setProjectId] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveTo, setEffectiveTo] = useState("");

  const [laborRows, setLaborRows] = useState<DraftLabor[]>([]);
  const [materialRows, setMaterialRows] = useState<DraftMaterial[]>([]);
  const [equipmentRows, setEquipmentRows] = useState<DraftEquipment[]>([]);

  const addLabor = () =>
    setLaborRows((p) => [
      ...p,
      { uid: uid(), craft: "", billing_method: "stipulated", regular_rate: "", ot_multiplier: "1.5", dt_multiplier: "2.0", markup_percent: "" },
    ]);

  const addMaterial = () =>
    setMaterialRows((p) => [...p, { uid: uid(), category: "", markup_percent: "15", tax_billable: false }]);

  const addEquipment = () =>
    setEquipmentRows((p) => [...p, { uid: uid(), equipment_class: "", rate_basis: "daily", rate: "" }]);

  const create = useMutation({
    mutationFn: async () => {
      const body = {
        name,
        description: description || null,
        rate_type: rateType,
        project_id: projectId || null,
        effective_from: effectiveFrom,
        effective_to: effectiveTo || null,
        labor: laborRows.map(({ uid: _u, ...l }) => ({
          craft: l.craft,
          billing_method: l.billing_method,
          regular_rate: l.billing_method === "stipulated" ? l.regular_rate : null,
          ot_multiplier: l.ot_multiplier || "1.5",
          dt_multiplier: l.dt_multiplier || "2.0",
          markup_percent: l.billing_method === "cost_plus" ? l.markup_percent : null,
        })),
        materials: materialRows.map(({ uid: _u, ...m }) => ({
          category: m.category || null,
          markup_percent: m.markup_percent || "0",
          tax_billable: m.tax_billable,
        })),
        equipment: equipmentRows.map(({ uid: _u, ...e }) => ({
          equipment_class: e.equipment_class,
          rate_basis: e.rate_basis,
          rate: e.rate || "0",
        })),
      };
      return api<RateSheet>("/api/money/rate-sheets", { method: "POST", body });
    },
    onSuccess: (sheet) => {
      qc.invalidateQueries({ queryKey: rateSheetKeys.all });
      toast.success("Rate sheet created");
      navigate({ to: "/rate-sheets/$rateSheetId", params: { rateSheetId: sheet.id } });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const canSubmit = name && effectiveFrom;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <Typography variant="h5">New Rate Sheet</Typography>
        <div className="flex gap-2">
          <Button variant="outlined" onClick={() => navigate({ to: "/rate-sheets" })}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!canSubmit || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Saving…" : "Create Rate Sheet"}
          </Button>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white rounded-lg border border-slate-200 p-5 dark:bg-gray-900 dark:border-gray-700">
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Header</Typography>
        <div className="grid grid-cols-2 gap-4">
          <TextField label="Name *" value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth />
          <TextField select label="Rate Type" value={rateType} onChange={(e) => setRateType(e.target.value)} size="small">
            <MenuItem value="stipulated">Stipulated</MenuItem>
            <MenuItem value="cost_plus">Cost Plus</MenuItem>
            <MenuItem value="hybrid">Hybrid</MenuItem>
          </TextField>
          <ProjectIdSelect
            label="Project (optional)" value={projectId}
            onChange={setProjectId} statusFilter={STANDARD_PROJECT_STATUSES}
            size="small" fullWidth placeholder="Entity-wide default…"
          />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} size="small" fullWidth />
          <TextField label="Effective From *" type="date" value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
          <TextField label="Effective To" type="date" value={effectiveTo}
            onChange={(e) => setEffectiveTo(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
        </div>
      </div>

      {/* Labor */}
      <div className="bg-white rounded-lg border border-slate-200 p-5 dark:bg-gray-900 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <Typography variant="subtitle1" fontWeight={600}>Labor Rates</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addLabor}>Add Craft</Button>
        </div>
        {laborRows.length === 0 && <p className="text-sm text-slate-400">No labor rates yet.</p>}
        <div className="space-y-3">
          {laborRows.map((l) => (
            <div key={l.uid} className="flex gap-2 items-start flex-wrap">
              <TextField label="Craft" value={l.craft} size="small"
                onChange={(e) => setLaborRows((rows) => rows.map((r) => r.uid === l.uid ? { ...r, craft: e.target.value } : r))}
                sx={{ flex: 2, minWidth: 140 }} />
              <TextField label="Method" value={l.billing_method} size="small" select
                onChange={(e) => setLaborRows((rows) => rows.map((r) => r.uid === l.uid ? { ...r, billing_method: e.target.value } : r))}
                sx={{ width: 130 }}>
                <MenuItem value="stipulated">Stipulated</MenuItem>
                <MenuItem value="cost_plus">Cost Plus</MenuItem>
              </TextField>
              {l.billing_method === "stipulated" ? (
                <MoneyField label="Rate / hr" value={l.regular_rate} size="small"
                  onChange={(v) => setLaborRows((rows) => rows.map((r) => r.uid === l.uid ? { ...r, regular_rate: v } : r))}
                  sx={{ width: 110 }} />
              ) : (
                <TextField label="Markup %" value={l.markup_percent} size="small" type="number"
                  onChange={(e) => setLaborRows((rows) => rows.map((r) => r.uid === l.uid ? { ...r, markup_percent: e.target.value } : r))}
                  sx={{ width: 110 }} />
              )}
              <TextField label="OT ×" value={l.ot_multiplier} size="small" type="number"
                onChange={(e) => setLaborRows((rows) => rows.map((r) => r.uid === l.uid ? { ...r, ot_multiplier: e.target.value } : r))}
                sx={{ width: 80 }} />
              <TextField label="DT ×" value={l.dt_multiplier} size="small" type="number"
                onChange={(e) => setLaborRows((rows) => rows.map((r) => r.uid === l.uid ? { ...r, dt_multiplier: e.target.value } : r))}
                sx={{ width: 80 }} />
              <IconButton size="small" color="error"
                onClick={() => setLaborRows((rows) => rows.filter((r) => r.uid !== l.uid))}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          ))}
        </div>
      </div>

      {/* Materials */}
      <div className="bg-white rounded-lg border border-slate-200 p-5 dark:bg-gray-900 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <Typography variant="subtitle1" fontWeight={600}>Material Rules</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addMaterial}>Add Rule</Button>
        </div>
        <p className="text-xs text-slate-400 mb-2">Leave category blank for a catch-all rule.</p>
        {materialRows.length === 0 && <p className="text-sm text-slate-400">No material rules yet.</p>}
        <div className="space-y-3">
          {materialRows.map((m) => (
            <div key={m.uid} className="flex gap-2 items-center">
              <TextField label="Category (blank = catch-all)" value={m.category} size="small"
                onChange={(e) => setMaterialRows((rows) => rows.map((r) => r.uid === m.uid ? { ...r, category: e.target.value } : r))}
                sx={{ flex: 2 }} />
              <TextField label="Markup %" value={m.markup_percent} size="small" type="number"
                onChange={(e) => setMaterialRows((rows) => rows.map((r) => r.uid === m.uid ? { ...r, markup_percent: e.target.value } : r))}
                sx={{ width: 110 }} />
              <TextField label="Tax billable?" value={m.tax_billable ? "yes" : "no"} size="small" select
                onChange={(e) => setMaterialRows((rows) => rows.map((r) => r.uid === m.uid ? { ...r, tax_billable: e.target.value === "yes" } : r))}
                sx={{ width: 120 }}>
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </TextField>
              <IconButton size="small" color="error"
                onClick={() => setMaterialRows((rows) => rows.filter((r) => r.uid !== m.uid))}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          ))}
        </div>
      </div>

      {/* Equipment */}
      <div className="bg-white rounded-lg border border-slate-200 p-5 dark:bg-gray-900 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <Typography variant="subtitle1" fontWeight={600}>Equipment Rates</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addEquipment}>Add Rate</Button>
        </div>
        {equipmentRows.length === 0 && <p className="text-sm text-slate-400">No equipment rates yet.</p>}
        <div className="space-y-3">
          {equipmentRows.map((e) => (
            <div key={e.uid} className="flex gap-2 items-start">
              <TextField label="Equipment Class" value={e.equipment_class} size="small"
                onChange={(ev) => setEquipmentRows((rows) => rows.map((r) => r.uid === e.uid ? { ...r, equipment_class: ev.target.value } : r))}
                sx={{ flex: 3 }} />
              <TextField label="Rate Basis" value={e.rate_basis} size="small" select
                onChange={(ev) => setEquipmentRows((rows) => rows.map((r) => r.uid === e.uid ? { ...r, rate_basis: ev.target.value } : r))}
                sx={{ width: 130 }}>
                {["hourly", "daily", "weekly", "monthly"].map((b) => (
                  <MenuItem key={b} value={b}>{b.charAt(0).toUpperCase() + b.slice(1)}</MenuItem>
                ))}
              </TextField>
              <MoneyField label="Rate" value={e.rate} size="small"
                onChange={(v) => setEquipmentRows((rows) => rows.map((r) => r.uid === e.uid ? { ...r, rate: v } : r))}
                sx={{ width: 100 }} />
              <IconButton size="small" color="error"
                onClick={() => setEquipmentRows((rows) => rows.filter((r) => r.uid !== e.uid))}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
